import { capitalizeFirstLetter } from '@repo/editor/src/utils/string';
import { withEventScope } from '@repo/event-processor';
import { IconButton } from '@repo/ui/components/Buttons';
import DeleteIcon from '@repo/ui/components/Icon/DeleteIcon';
import ResendIcon from '@repo/ui/components/Icon/ResendIcon';
import { fireToast } from '@repo/ui/components/Toast';
import { themeStyle } from '@repo/ui/theming';

import teamsApi from 'global/teamsApi';

import { Container } from './styles';
import { InviteButtonsProps } from './types';

const theme = {
  color: themeStyle.varInkMedium,
  activeColor: themeStyle.varInkMain,
  activeDefaultColor: themeStyle.varInkMain,
  transition: 'all 0.2s ease-in-out',
};

const formatError = (error: string): string => {
  const formatedError = capitalizeFirstLetter(
    error.replaceAll('_', ' ').toLowerCase()
  );
  if (
    ['FORBIDDEN', 'NOT_FOUND', 'UNAUTHORIZED', 'SENT_TO_MANY_INVITES'].includes(
      error
    )
  ) {
    return `${formatedError}. Please contact support.`;
  }
  return `${formatedError}. Try again.`;
};

/**
 * buttons to trigger actions on sent invites
 */
const InviteButtons: React.FC<InviteButtonsProps> = ({
  email,
  onDelete,
  displayVertical,
}) => {
  const handleResend = async (): Promise<void> => {
    const response = await teamsApi.resendTeamInvites([email]);
    if (response.error) {
      fireToast({
        error: true,
        label: `Failed to resend invite for ${email}. ${formatError(
          response.error
        )}`,
      });
    } else {
      fireToast({
        label: `Resent invite to ${email}.`,
      });
    }
  };

  const handleDelete = async (): Promise<void> => {
    const response = await teamsApi.deleteTeamInvites([email]);
    if (response.error) {
      fireToast({
        error: true,
        label: `Failed to delete invite for ${email}. ${formatError(
          response.error
        )}`,
      });
    } else {
      fireToast({
        label: `Deleted invite for ${email}.`,
      });
    }
    onDelete(email);
  };

  return (
    <Container displayVertical={!!displayVertical} gap="8px">
      <IconButton
        ariaLabel="Resend Invite"
        height="16px"
        icon={ResendIcon}
        name="resendInviteButton"
        theme={theme}
        tooltipText="Resend Invite"
        onClick={handleResend}
      />
      <IconButton
        ariaLabel="Delete Invite"
        height="16px"
        icon={DeleteIcon}
        name="deleteInviteButton"
        theme={theme}
        tooltipText="Delete Invite"
        onClick={handleDelete}
      />
    </Container>
  );
};

export default withEventScope(InviteButtons, { module: 'inviteButtons' });
